/**
 * TrippyPostFX — Phaser 4 camera filter for the game-juice pass.
 *
 * Architecture: Phaser 4 replaced the Phaser 3 `PostFXPipeline` system with a
 * `Filters.Controller` + `RenderNodes.BaseFilterShader` pair. The `Controller`
 * holds per-frame state (uniforms) and is attached to a camera's FilterList.
 * The `BaseFilterShader` subclass handles GLSL compilation and uniform upload.
 *
 * Usage (Task 15):
 *   const ctrl = new TrippyPostFX(camera);
 *   camera.filters.external.add(ctrl);
 *
 * Registration (BootScene, Task 14):
 *   (renderer as Phaser.Renderer.WebGL.WebGLRenderer)
 *     .renderNodes.addNodeConstructor('FilterTrippyPostFX', FilterTrippyPostFX);
 */

import Phaser from 'phaser';

// ---------------------------------------------------------------------------
// Fragment shader
// ---------------------------------------------------------------------------
const FRAG = /* glsl */ `
// TRIPPYPOSTFX_FS
#pragma phaserTemplate(shaderName)

#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform vec2  uResolution;
uniform float uHue;          // radians
uniform float uInvertMix;    // 0..1
uniform float uAberration;   // px
uniform float uVignette;     // 0..1
uniform float uSat;          // saturation multiplier (1 = identity)
uniform float uTimeMs;

varying vec2 outTexCoord;

#pragma phaserTemplate(fragmentHeader)

vec3 hueRotate(vec3 c, float a) {
  const mat3 toYIQ = mat3(0.299, 0.587, 0.114, 0.596, -0.275, -0.321, 0.212, -0.523, 0.311);
  const mat3 toRGB = mat3(1.0, 0.956, 0.621, 1.0, -0.272, -0.647, 1.0, -1.106, 1.703);
  vec3 yiq = toYIQ * c;
  float cs = cos(a);
  float sn = sin(a);
  yiq.yz = vec2(yiq.y * cs - yiq.z * sn, yiq.y * sn + yiq.z * cs);
  return toRGB * yiq;
}

void main () {
  vec2 uv = outTexCoord;

  // Chromatic aberration — split R/B horizontally
  float aPx = uAberration / max(uResolution.x, 1.0);
  vec4 sampleR = boundedSampler(uMainSampler, vec2(uv.x + aPx, uv.y));
  vec4 sampleG = boundedSampler(uMainSampler, uv);
  vec4 sampleB = boundedSampler(uMainSampler, vec2(uv.x - aPx, uv.y));
  vec3 col = vec3(sampleR.r, sampleG.g, sampleB.b);

  // Hue rotation
  if (abs(uHue) > 0.001) col = hueRotate(col, uHue);

  // Saturation
  float l = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(vec3(l), col, uSat);

  // Invert mix
  if (uInvertMix > 0.001) col = mix(col, vec3(1.0) - col, uInvertMix);

  // Vignette with horizontal jitter
  if (uVignette > 0.001) {
    vec2 cc = uv - 0.5;
    float jitter = sin(uTimeMs * 0.05 + uv.y * 80.0) * uVignette * 0.01;
    cc.x += jitter;
    float d = dot(cc, cc);
    float vig = 1.0 - smoothstep(0.15, 0.6, d) * uVignette;
    col *= vig;
  }

  gl_FragColor = vec4(col, sampleG.a);
}
`;

// ---------------------------------------------------------------------------
// RenderNode — GLSL compilation + uniform upload
// ---------------------------------------------------------------------------

// Re-export the base class under a local alias so we can extend it without
// TypeScript complaining about the abstract/non-instantiable declaration.
const BaseFilterShaderCtor = Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader as unknown as new (
  name: string,
  manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager,
  fragmentShaderKey: string | undefined,
  fragmentShaderSource: string,
  shaderAdditions?: Phaser.Types.Renderer.WebGL.ShaderAdditionConfig[]
) => Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader;

/**
 * FilterTrippyPostFX is the RenderNode subclass that Phaser's render pipeline
 * calls to execute the shader. It must be registered with the
 * `RenderNodeManager` before any `TrippyPostFX` Controller is added to a
 * camera's FilterList. Registration happens in BootScene.
 */
export class FilterTrippyPostFX extends BaseFilterShaderCtor {
  constructor(manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager) {
    super('FilterTrippyPostFX', manager, undefined, FRAG);
  }

  setupUniforms(
    controller: Phaser.Filters.Controller,
    _drawingContext: Phaser.Renderer.WebGL.DrawingContext
  ): void {
    const ctrl = controller as TrippyPostFX;
    const pm = (this as unknown as { programManager: Phaser.Renderer.WebGL.ProgramManager }).programManager;
    pm.setUniform('uHue', ctrl.uHue);
    pm.setUniform('uInvertMix', ctrl.uInvertMix);
    pm.setUniform('uAberration', ctrl.uAberration);
    pm.setUniform('uVignette', ctrl.uVignette);
    pm.setUniform('uSat', ctrl.uSat);
    pm.setUniform('uTimeMs', ctrl.uTimeMs);
    pm.setUniform('uResolution', [ctrl.camera.width, ctrl.camera.height]);
  }
}

// ---------------------------------------------------------------------------
// Controller — holds per-frame state
// ---------------------------------------------------------------------------

/**
 * TrippyPostFX is the Filters.Controller that holds the uniform values.
 * Attach it to a camera's external filter list in GameScene (Task 15):
 *
 *   const ctrl = new TrippyPostFX(this.cameras.main);
 *   this.cameras.main.filters.external.add(ctrl);
 *
 * At rest all uniforms are identity values — no visible effect.
 */
export class TrippyPostFX extends (
  Phaser.Filters.Controller as unknown as new (
    camera: Phaser.Cameras.Scene2D.Camera,
    renderNode: string
  ) => Phaser.Filters.Controller
) {
  public uHue = 0;
  public uInvertMix = 0;
  public uAberration = 0;
  public uVignette = 0;
  public uSat = 1.0;
  public uTimeMs = 0;

  constructor(camera: Phaser.Cameras.Scene2D.Camera) {
    super(camera, 'FilterTrippyPostFX');
  }
}
